import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { rbacService } from '../services/rbacService';
import { Role, UserRole, AssignRoleData } from '../types/rbac';
import PageWrapper from '../components/common/PageWrapper';
import { masterDropdownService } from '../services/masterDropdownService';
import {
  fetchUserRoles,
  assignRoleToUser,
  removeRoleFromUser,
  fetchUserPermissionsSummary,
  fetchRoles,
} from '../store/slices/rbacSlice';
import { fetchAllUsers } from '../feature/user/userSlice';
import { fetchBranches } from '../feature/branch/branchSlice';
import { PermissionGuard } from '../components/common/PermissionGuard';
import BaseModal from '../components/common/BaseModal';
import { AVAILABLE_REPORTS } from '../config/reports';

interface Permission {
  id: string;
  name: string;
  description: string;
  module: string;
  action: string;
  is_system_permission: boolean;
  parent_module?: string | null;
}

interface ModulePermissions {
  [module: string]: {
    [action: string]: boolean;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`role-tabpanel-${index}`}
      aria-labelledby={`role-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const RolePermissions: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { userRoles, userPermissions } = useSelector((state: RootState) => state.rbac);
  const { users } = useSelector((state: RootState) => state.users);
  const { branches } = useSelector((state: RootState) => state.branches);
  const { roles: reduxRoles } = useSelector((state: RootState) => state.rbac);
  
  const [activeTab, setActiveTab] = useState(0);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [rolePermissions, setRolePermissions] = useState<ModulePermissions>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [systemRoleNames, setSystemRoleNames] = useState<string[]>([]);
  
  // User assignment state
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignData, setAssignData] = useState<AssignRoleData>({
    userId: '',
    roleId: '',
    branchId: '',
  });

  // Derive modules and parent/child relationships from backend permissions
  const uniqueModules = React.useMemo(() => {
    const set = new Set<string>();
    permissions.forEach((p) => {
      if (p.module) set.add(p.module);
    });
    return Array.from(set);
  }, [permissions]);

  const moduleToParent = React.useMemo(() => {
    const map: Record<string, string> = {};
    permissions.forEach((p) => {
      if (p.module && p.parent_module) {
        map[p.module] = p.parent_module;
      }
    });
    return map;
  }, [permissions]);

  const parentToChildren = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    permissions.forEach((p) => {
      if (p.parent_module && p.module) {
        if (!map[p.parent_module]) map[p.parent_module] = [];
        if (!map[p.parent_module].includes(p.module)) {
          map[p.parent_module].push(p.module);
        }
      }
    });
    return map;
  }, [permissions]);

  const sortedModules = React.useMemo(() => {
    const parents = uniqueModules.filter((m) => !moduleToParent[m]);
    const order: string[] = [];
    parents.forEach((parent) => {
      order.push(parent);
      const children = (parentToChildren[parent] || []).slice().sort();
      children.forEach((c) => {
        if (!order.includes(c)) order.push(c);
      });
    });
    uniqueModules.forEach((m) => {
      if (!order.includes(m)) order.push(m);
    });
    return order;
  }, [uniqueModules, moduleToParent, parentToChildren]);

  const actions = ['create', 'read', 'update', 'delete', 'manage_roles'];

  useEffect(() => {
    loadData();
    if (activeTab === 1) {
      dispatch(fetchAllUsers());
      dispatch(fetchBranches());
      dispatch(fetchRoles());
    }
  }, [dispatch, activeTab]);

  // Load role types from master
  useEffect(() => {
    const loadRoleTypes = async () => {
      try {
        const roleTypeOptions = await masterDropdownService.getRoleTypesDropdown();
        const roleNames = roleTypeOptions.map(option => String(option.value));
        setSystemRoleNames(roleNames);
      } catch (err) {
        console.error('Failed to load role types from master:', err);
        setSystemRoleNames(['admin', 'super_admin', 'branch_admin', 'manager', 'supervisor', 'worker', 'sales_person']);
      }
    };
    loadRoleTypes();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [rolesResponse, permissionsResponse] = await Promise.all([
        rbacService.getAllRoles(),
        rbacService.getAllPermissions(),
      ]);

      setRoles(rolesResponse);
      setPermissions(permissionsResponse.permissions);

      if (rolesResponse.length > 0) {
        setSelectedRole(rolesResponse[0].id);
        setRolePermissions(rolesResponse[0].permissions);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    setError('');
    setSuccess('Syncing role types from Masters and refreshing...');
    
    try {
      const syncResult = await rbacService.syncRoleTypesFromMaster();
      console.log('Sync result:', syncResult);
      
      const roleTypeOptions = await masterDropdownService.getRoleTypesDropdown();
      const roleNames = roleTypeOptions.map(option => String(option.value));
      setSystemRoleNames(roleNames);
      
      await loadData();
      
      const syncMessage = syncResult.created.length > 0
        ? `Synced ${syncResult.created.length} new role(s) from Masters: ${syncResult.created.map(r => r.name).join(', ')}`
        : 'All role types are already synced.';
      setSuccess(syncMessage);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      console.error('Failed to refresh role types from master:', err);
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to refresh role types');
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (roleId: string) => {
    setSelectedRole(roleId);
    const role = roles.find(r => r.id === roleId);
    if (role) {
      setRolePermissions(role.permissions);
    }
  };

  const handlePermissionChange = (module: string, action: string, checked: boolean) => {
    setRolePermissions(prev => {
      const next: ModulePermissions = {
        ...prev,
        [module]: {
          ...prev[module],
          [action]: checked,
        },
      };

      const parent = module;
      const isParentModule = !!parentToChildren[parent];
      if (isParentModule && action === 'read' && checked === false) {
        const children = parentToChildren[parent] || [];
        children.forEach((child) => {
          if (!next[child]) return;
          const updatedChild: Record<string, boolean> = {} as any;
          Object.keys(next[child]).forEach((act) => {
            updatedChild[act] = false;
          });
          next[child] = updatedChild as any;
        });
      }

      return next;
    });
  };

  const handleReportPermissionChange = (reportId: string, checked: boolean) => {
    setRolePermissions(prev => {
      const reportsModule = prev['reports'] || {};
      const existingReportPermission = reportsModule[reportId];
      // Ensure we only spread if it's an object, not a boolean
      const existingPermissionObj = existingReportPermission && typeof existingReportPermission === 'object' 
        ? existingReportPermission 
        : {};
      
      return {
        ...prev,
        reports: {
          ...reportsModule,
          [reportId]: {
            ...existingPermissionObj,
            read: checked,
          },
        } as any, // Type assertion needed because reports can contain nested objects for individual reports
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const role = roles.find(r => r.id === selectedRole);
      if (!role) return;

      const isSystemRole = role.is_system_role || systemRoleNames.includes(role.name);
      
      const updateData = isSystemRole 
        ? { permissions: rolePermissions }
        : { permissions: rolePermissions };

      await rbacService.updateRole(selectedRole, updateData);

      setSuccess('Role permissions updated successfully');
      
      setRoles(prev => prev.map(r => 
        r.id === selectedRole 
          ? { ...r, permissions: rolePermissions }
          : r
      ));

      const { refreshPermissions } = await import('../store/slices/authSlice');
      (dispatch as any)(refreshPermissions());
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const getModuleDisplayName = (module: string) => {
    const displayNames: Record<string, string> = {
      users: 'User Management',
      customers: 'Customer Management',
      orders: 'Order Management',
      branches: 'Branch Management',
      sites: 'Site Management',
      workflows: 'Workflow Management',
      'workflow assignments': 'Workflow Assignments',
      dashboard: 'Dashboard',
      realtime_dashboard: 'Real-Time Dashboard',
      admin_stage_status: 'Work Status',
      reports: 'Reports',
      settings: 'System Settings / Notification Preferences (Master)',
      stages: 'Stages Management (Master)',
      rbac: 'RBAC Management',
      count_master: 'Count Master (Master)',
      master: 'Masters (Parent Access)',
      states: 'States (Master)',
      cities: 'Cities (Master)',
      colors: 'Colors (Master)',
      sets: 'Sets (Master)',
      items: 'Items (Master)',
      item_subtype_definitions: 'Item Subtype Definitions (Master)',
    };
    return displayNames[module] || module;
  };

  const getActionDisplayName = (action: string) => {
    const displayNames: Record<string, string> = {
      create: 'Create',
      read: 'Read',
      update: 'Update',
      delete: 'Delete',
      manage_roles: 'Manage Roles',
    };
    return displayNames[action] || action;
  };

  const isActionAvailable = (module: string, action: string) => {
    if (action === 'manage_roles' && module !== 'users') {
      return false;
    }
    return true;
  };

  // User assignment handlers
  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    if (userId && userId !== 'undefined' && userId !== '') {
      dispatch(fetchUserRoles(userId));
      dispatch(fetchUserPermissionsSummary(userId));
    }
  };

  const handleAssignRole = async () => {
    try {
      await dispatch(assignRoleToUser(assignData));
      setShowAssignModal(false);
      
      const userId = assignData.userId;
      setAssignData({ userId: '', roleId: '', branchId: '' });
      
      if (userId && userId !== 'undefined' && userId !== '') {
        dispatch(fetchUserRoles(userId));
        dispatch(fetchUserPermissionsSummary(userId));
      }
    } catch (error) {
      console.error('Failed to assign role:', error);
    }
  };

  const handleRemoveRole = async (userRole: UserRole) => {
    if (window.confirm('Are you sure you want to remove this role assignment?')) {
      try {
        await dispatch(
          removeRoleFromUser({
            userId: userRole.user_id,
            roleId: userRole.role_id,
            branchId: userRole.branch_id,
          })
        );
        if (userRole.user_id && userRole.user_id !== 'undefined' && userRole.user_id !== '') {
          dispatch(fetchUserRoles(userRole.user_id));
          dispatch(fetchUserPermissionsSummary(userRole.user_id));
        }
      } catch (error) {
        console.error('Failed to remove role:', error);
      }
    }
  };

  const selectedUser = users.find((user) => user.id === selectedUserId);
  const selectedUserRoles = userRoles[selectedUserId] || [];
  const selectedUserPermissions = userPermissions[selectedUserId];

  if (loading && roles.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageWrapper>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Role & Permission Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure module access and actions for each role. Manage user role assignments.
          </Typography>
        </Box>
        <Tooltip title="Refresh role types from Masters">
          <IconButton 
            onClick={refreshData} 
            disabled={loading}
            color="primary"
            aria-label="refresh role types"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Roles & Permissions" />
            <Tab label="Users" />
          </Tabs>
        </Box>

        {/* Roles & Permissions Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box display="grid" sx={{ gap: 3, gridTemplateColumns: { xs: '1fr', md: '300px 1fr' } }}>
            {/* Role Selection */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Select Role
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={selectedRole}
                      label="Role"
                      onChange={(e) => handleRoleChange(e.target.value)}
                      disabled={loading}
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {role.name}
                            {role.is_system_role && (
                              <Chip label="System" size="small" color="primary" />
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedRole && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {roles.find(r => r.id === selectedRole)?.description}
                      </Typography>
                      
                      {roles.find(r => r.id === selectedRole)?.is_system_role && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          This is a system role. You can modify permissions but not the role name or description.
                        </Alert>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Permissions Matrix */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Permission Matrix
                  </Typography>

                  {selectedRole && roles.length > 0 ? (
                    <Box>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper' }}>
                                Module
                              </TableCell>
                              {actions.map((action) => (
                                <TableCell key={action} align="center" sx={{ fontWeight: 'bold' }}>
                                  {getActionDisplayName(action)}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedModules.map((module) => {
                              const parent = moduleToParent[module];
                              const parentReadEnabled = parent ? (rolePermissions[parent]?.read === true) : true;
                              const isChild = !!parent;
                              const shouldShow = !isChild || parentReadEnabled;
                              const isReportsModule = module === 'reports';

                              return (
                                <React.Fragment key={module}>
                                  <TableRow sx={{ 
                                    backgroundColor: isChild ? 'action.hover' : 'background.paper',
                                    '&:hover': { backgroundColor: 'action.selected' }
                                  }}>
                                    <TableCell 
                                      sx={{ 
                                        fontWeight: isChild ? 'normal' : 'bold',
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 2,
                                        backgroundColor: isChild ? 'action.hover' : 'background.paper',
                                        pl: isChild ? 4 : 2
                                      }}
                                    >
                                      {getModuleDisplayName(module)}
                                      {isChild && (
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                          (Child of {getModuleDisplayName(parent)})
                                        </Typography>
                                      )}
                                    </TableCell>
                                    {actions.map((action) => {
                                      if (!isActionAvailable(module, action)) return null;
                                      
                                      const isChecked = rolePermissions[module]?.[action] || false;
                                      const isDisabled = roles.find(r => r.id === selectedRole)?.is_system_role || (isChild && !parentReadEnabled);

                                      return (
                                        <TableCell key={action} align="center">
                                          {!shouldShow && action === 'read' ? (
                                            <Typography variant="caption" color="text.secondary">
                                              Enable {getModuleDisplayName(parent)} Read first
                                            </Typography>
                                          ) : (
                                            <Checkbox
                                              checked={isChecked}
                                              onChange={(e) => handlePermissionChange(module, action, e.target.checked)}
                                              disabled={isDisabled}
                                              size="small"
                                            />
                                          )}
                                        </TableCell>
                                      );
                                    })}
                                  </TableRow>
                                  
                                  {/* Show individual reports when reports module is visible */}
                                  {isReportsModule && (
                                    <>
                                      {AVAILABLE_REPORTS.map((report) => {
                                        const reportPermissionRaw = rolePermissions[module]?.[report.id];
                                        // reportPermission can be an object with read property, undefined, null, or boolean
                                        let isReportChecked = false;
                                        if (reportPermissionRaw && typeof reportPermissionRaw === 'object' && reportPermissionRaw !== null) {
                                          const reportPermission = reportPermissionRaw as { read?: boolean };
                                          isReportChecked = reportPermission.read === true;
                                        }
                                        const isDisabled = roles.find(r => r.id === selectedRole)?.is_system_role;

                                        return (
                                          <TableRow key={report.id} sx={{ 
                                            backgroundColor: 'action.hover',
                                            '&:hover': { backgroundColor: 'action.selected' }
                                          }}>
                                            <TableCell 
                                              sx={{ 
                                                pl: 6,
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 2,
                                                backgroundColor: 'action.hover',
                                              }}
                                            >
                                              <Typography variant="body2" color="text.secondary">
                                                • {report.name}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="center" colSpan={actions.length}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={isReportChecked}
                                                    onChange={(e) => handleReportPermissionChange(report.id, e.target.checked)}
                                                    disabled={isDisabled}
                                                    size="small"
                                                  />
                                                }
                                                label="Read"
                                                sx={{ m: 0 }}
                                              />
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {!roles.find(r => r.id === selectedRole)?.is_system_role && (
                        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                          <Button
                            variant="contained"
                            onClick={handleSavePermissions}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={20} /> : null}
                          >
                            {saving ? 'Saving...' : 'Save Permissions'}
                          </Button>
                          
                          <Button
                            variant="outlined"
                            onClick={() => {
                              const role = roles.find(r => r.id === selectedRole);
                              if (role) {
                                setRolePermissions(role.permissions);
                              }
                            }}
                          >
                            Reset Changes
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : roles.length === 0 ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <Typography color="text.secondary">
                        No roles available
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Please select a role to configure permissions.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box display="grid" sx={{ gap: 3, gridTemplateColumns: { xs: '1fr', lg: '300px 1fr' } }}>
            {/* User Selection */}
            <Box>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Select User
                    </Typography>
                    <PermissionGuard module="users" action="manage_roles">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setShowAssignModal(true)}
                      >
                        Assign Role
                      </Button>
                    </PermissionGuard>
                  </Box>
                  <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                    {users.map((user) => (
                      <Box
                        key={user.id}
                        onClick={() => handleUserSelect(user.id)}
                        sx={{
                          p: 2,
                          mb: 1,
                          border: 1,
                          borderColor: selectedUserId === user.id ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          cursor: 'pointer',
                          backgroundColor: selectedUserId === user.id ? 'primary.light' : 'background.paper',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'capitalize' }}>
                          {user.role}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* User Roles and Permissions */}
            <Box>
              {selectedUser ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Roles for {selectedUser.firstName} {selectedUser.lastName}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {selectedUserRoles.map((userRole) => {
                          // Handle both UserRole interface and backend response format
                          const roleName = (userRole as any).roleName || userRole.role?.name || 'Unknown Role';
                          const roleDescription = (userRole as any).roleDescription || userRole.role?.description || '';
                          const branchName = (userRole as any).branchName || userRole.branch?.name || '';
                          
                          return (
                            <Paper key={userRole.id} variant="outlined" sx={{ p: 2 }}>
                              <Box display="flex" justifyContent="space-between" alignItems="start">
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {roleName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {roleDescription}
                                  </Typography>
                                  {(userRole.branch_id || branchName) && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Branch: {branchName}
                                    </Typography>
                                  )}
                                </Box>
                                <PermissionGuard module="users" action="manage_roles">
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveRole(userRole)}
                                  >
                                    Remove
                                  </Button>
                                </PermissionGuard>
                              </Box>
                            </Paper>
                          );
                        })}
                        {selectedUserRoles.length === 0 && (
                          <Typography color="text.secondary" textAlign="center" py={2}>
                            No roles assigned
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* User Permissions Summary */}
                  {selectedUserPermissions && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Effective Permissions
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Module</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Create</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Read</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Update</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Delete</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Manage Roles</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(selectedUserPermissions.effectivePermissions || {}).map(([module, permissions]: [string, any]) => (
                                <TableRow key={module}>
                                  <TableCell sx={{ fontWeight: 'medium' }}>
                                    {getModuleDisplayName(module)}
                                  </TableCell>
                                  <TableCell align="center">
                                    {permissions.create ? '✓' : '-'}
                                  </TableCell>
                                  <TableCell align="center">
                                    {permissions.read ? '✓' : '-'}
                                  </TableCell>
                                  <TableCell align="center">
                                    {permissions.update ? '✓' : '-'}
                                  </TableCell>
                                  <TableCell align="center">
                                    {permissions.delete ? '✓' : '-'}
                                  </TableCell>
                                  <TableCell align="center">
                                    {permissions.manage_roles ? '✓' : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              ) : (
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" textAlign="center" py={4}>
                      Select a user to view their roles and permissions
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        </TabPanel>
      </Card>

      {/* Assign Role Modal */}
      <BaseModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title="Assign Role to User"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>User</InputLabel>
            <Select
              value={assignData.userId}
              label="User"
              onChange={(e) =>
                setAssignData((prev) => ({ ...prev, userId: e.target.value }))
              }
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={assignData.roleId}
              label="Role"
              onChange={(e) =>
                setAssignData((prev) => ({ ...prev, roleId: e.target.value }))
              }
            >
              {reduxRoles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name} - {role.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Branch (Optional)</InputLabel>
            <Select
              value={assignData.branchId || ''}
              label="Branch (Optional)"
              onChange={(e) =>
                setAssignData((prev) => ({ ...prev, branchId: e.target.value || undefined }))
              }
            >
              <MenuItem value="">No branch restriction</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setShowAssignModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAssignRole}
              disabled={!assignData.userId || !assignData.roleId}
            >
              Assign Role
            </Button>
          </Box>
        </Box>
      </BaseModal>
    </PageWrapper>
  );
};

export default RolePermissions;
